var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// (c) 2023 Acellera Ltd http://www.acellera.com
// All Rights Reserved
// No redistribution in whole or part
//
import { Box, CircularProgress, Typography } from "@mui/material";
import { darken, useTheme } from "@mui/material/styles";
import { MarkdownWrapper } from "../LLMChatMessages/MarkdownWrapper";
import { RoundActionIconButton } from "../../components/Buttons/ActionIconButton/RoundActionIconButton";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useState } from "react";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import { dispatchNotificationEvent } from "../../NotificationSystem";
import { updateMessageCodeBlock } from "./utils";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CachedIcon from "@mui/icons-material/Cached";
import { dispatchConfirmationDialogEvent } from "../../utils/EventDispatchers";
import EditIcon from "@mui/icons-material/Edit";
import { EditAnswerMarkdown } from "./EditAnswerMarkdown";
import { LLMMessageWrapper } from "./LLMMessageWrappers";
const AssistantContent = ({ message, handleLoadSMILES, waiting, completing, isLastMessage, setMessageFeedback, updateMessage, regenerateAnswer, }) => {
    const messageUncompleted = isLastMessage && completing;
    const detectSMILES = (markdown) => {
        const regex = /<smiles>([\s\S]*?)<\/smiles>/g;
        return markdown.replace(regex, (match, content) => {
            return `[\`${content}\`](_custom?type=smiles)`;
        });
    };
    let isThinking = false;
    const isError = message.error;
    const contentTextEl = message.content.find((e) => e.type === "text");
    if (!contentTextEl)
        return null;
    const contentText = contentTextEl.text || "";
    let thinking = "";
    let answer = "";
    if (isError) {
        answer = contentText;
    }
    else {
        const regex = /<(think|tool|answer)>([\s\S]*?)(?=<\/\1>|<\/(?:think|tool|answer)>|<(?:think|tool|answer)>|$)/g;
        const segments = [];
        let match;
        let endMatchIdx = 0;
        while ((match = regex.exec(contentText)) !== null) {
            segments.push({
                type: match[1],
                content: match[2].trim(),
            });
            endMatchIdx = match.index + match[0].length;
        }
        segments.forEach((segment) => {
            if (segment.type === "think") {
                thinking += (thinking ? "\n\n" : "") + segment.content;
            }
            else if (segment.type === "tool") {
                let name = "tool";
                try {
                    const data = JSON.parse(segment.content);
                    if (data.name)
                        name = data.name;
                }
                catch (_a) { }
                thinking += `\n\nUsing ${name}…`;
            }
            else if (segment.type === "answer") {
                const segmentContent = answer
                    ? `\n\n${segment.content}`
                    : segment.content;
                answer += segmentContent;
            }
        });
        isThinking = !!thinking && !answer;
        if (contentText && !answer) {
            if (!thinking) {
                answer = contentText;
            }
            else if (contentText.length > endMatchIdx) {
                // Text after <think> or <tool>  not in <answer> tags
                answer = contentText
                    .slice(endMatchIdx)
                    .replace(/^<answer>|<\/answer>|<tool>|<\/tool>|<\think>|<\/think>$/g, "");
            }
        }
    }
    const theme = useTheme();
    const textColor = theme.palette.getContrastText("#383838");
    const [isExpanded, setIsExpanded] = useState(false);
    const thinkColor = darken(theme.palette.common.white, 0.1);
    const handleEditCode = (newCode, fromCodeIdx, toCodeIdx) => {
        if (fromCodeIdx === undefined || toCodeIdx === undefined) {
            dispatchNotificationEvent({
                message: "Error editing code.",
                type: "error",
            });
            return;
        }
        const _newCode = `\`\`\`python
${newCode}
\`\`\``;
        const newContentText = updateMessageCodeBlock(contentText, answer, fromCodeIdx, toCodeIdx, _newCode);
        updateMessage(newContentText, "edit_code");
    };
    const answerWithSMILES = detectSMILES(answer);
    const copyAssistantMessage = () => {
        navigator.clipboard.writeText(answerWithSMILES);
    };
    const [isEditing, setIsEditing] = useState(false);
    const handleAcceptAnswerEdit = (newAnswer) => {
        setIsEditing(false);
        const fromAnswerIdx = contentText.indexOf(answer);
        const newContentText = contentText.slice(0, fromAnswerIdx) +
            newAnswer +
            contentText.slice(fromAnswerIdx + answer.length);
        updateMessage(newContentText, "edit_answer");
    };
    return (_jsxs(Box, { children: [_jsxs(Box, Object.assign({ sx: {
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    fontSize: "0.7em",
                    color: thinkColor,
                    // borderRadius: theme.shape.borderRadius,
                    paddingLeft: theme.spacing(1),
                    // paddingTop: theme.spacing(1),
                    maxWidth: "100%",
                    // marginBottom: theme.spacing(1),
                    // fontSize: "0.8em",
                    wordWrap: "break-word",
                    whiteSpace: "pre-wrap",
                } }, { children: [_jsxs(Box, Object.assign({ sx: {
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "flex-start",
                            alignItems: "center",
                        } }, { children: [messageUncompleted && (waiting || isThinking) ? (_jsxs(Box, Object.assign({ sx: {
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "flex-start",
                                    alignItems: "center",
                                    gap: 1,
                                } }, { children: [_jsx(CircularProgress, { size: 12, sx: { color: thinkColor } }), _jsx(Typography, Object.assign({ color: thinkColor, variant: "caption" }, { children: "Thinking..." }))] }))) : thinking ? (_jsx(_Fragment, { children: _jsx(Typography, Object.assign({ color: thinkColor, variant: "caption" }, { children: "Thought process" })) })) : null, thinking ? (_jsx(RoundActionIconButton, Object.assign({ size: "small", tooltipMsg: "Hide", onClick: () => {
                                    setIsExpanded(!isExpanded);
                                }, baseColor: textColor }, { children: isExpanded ? (_jsx(ExpandLessIcon, { fontSize: "small" })) : (_jsx(ExpandMoreIcon, { fontSize: "small" })) }))) : null] })), isExpanded && (_jsx(Box, Object.assign({ sx: {
                            borderLeft: `1px solid ${darken(theme.palette.common.white, 0.3)}`,
                            paddingLeft: 1,
                            marginLeft: 2,
                            marginBottom: 1,
                        } }, { children: _jsx(Typography, Object.assign({ color: thinkColor, variant: "caption" }, { children: thinking })) })))] })), _jsx(Box, { children: isEditing ? (_jsx(EditAnswerMarkdown, { answer: answer, acceptAnswerEdit: handleAcceptAnswerEdit, cancelAnswerEdit: () => {
                        setIsEditing(false);
                    }, disabled: completing })) : (_jsxs(_Fragment, { children: [_jsx(LLMMessageWrapper, Object.assign({ isError: isError }, { children: _jsx(MarkdownWrapper, Object.assign({ handleLoadSMILES: handleLoadSMILES, completing: completing, handleEditCode: handleEditCode }, { children: answerWithSMILES })) })), !isError && (_jsx(AssistantContentButtons, { messageFeedback: message.feedback, setMessageFeedback: setMessageFeedback, disabled: completing, textColor: textColor, copyAssistantMessage: copyAssistantMessage, regenerateAnswer: regenerateAnswer, editAssistantAnswer: () => {
                                setIsEditing(true);
                            } }))] })) })] }));
};
const AssistantContentButtons = ({ messageFeedback, setMessageFeedback, disabled, textColor, copyAssistantMessage, regenerateAnswer, editAssistantAnswer, }) => {
    const [justCopied, setJustCopied] = useState(false);
    const handleCopy = () => {
        copyAssistantMessage();
        setJustCopied(true);
        setTimeout(() => setJustCopied(false), 1000);
    };
    const handleRegenerate = () => __awaiter(void 0, void 0, void 0, function* () {
        const ok = yield dispatchConfirmationDialogEvent({
            message: "This will delete the current and subsequent messages. Do you want to continue?",
        });
        if (ok)
            regenerateAnswer();
    });
    return (_jsxs(Box, { children: [_jsx(RoundActionIconButton, Object.assign({ size: "small", tooltipMsg: "Copy", onClick: () => {
                    handleCopy();
                }, baseColor: textColor, disabled: disabled }, { children: justCopied ? (_jsx(CheckIcon, { fontSize: "small" })) : (_jsx(ContentCopyIcon, { sx: { fontSize: "1rem" } })) })), _jsx(RoundActionIconButton, Object.assign({ size: "small", tooltipMsg: "Edit response", onClick: editAssistantAnswer, baseColor: textColor, disabled: disabled }, { children: _jsx(EditIcon, { fontSize: "small" }) })), messageFeedback === "good" ? (_jsx(RoundActionIconButton, Object.assign({ size: "small", tooltipMsg: "Good response", onClick: () => {
                    setMessageFeedback();
                }, baseColor: textColor, disabled: disabled }, { children: _jsx(ThumbUpAltIcon, { fontSize: "small" }) }))) : (_jsx(RoundActionIconButton, Object.assign({ size: "small", tooltipMsg: "Good response", onClick: () => {
                    setMessageFeedback("good");
                }, baseColor: textColor, disabled: disabled }, { children: _jsx(ThumbUpOffAltIcon, { fontSize: "small" }) }))), messageFeedback === "bad" ? (_jsx(RoundActionIconButton, Object.assign({ size: "small", tooltipMsg: "Bad response", onClick: () => {
                    setMessageFeedback();
                }, baseColor: textColor, disabled: disabled }, { children: _jsx(ThumbDownAltIcon, { fontSize: "small" }) }))) : (_jsx(RoundActionIconButton, Object.assign({ size: "small", tooltipMsg: "Bad response", onClick: () => {
                    setMessageFeedback("bad");
                }, baseColor: textColor, disabled: disabled }, { children: _jsx(ThumbDownOffAltIcon, { fontSize: "small" }) }))), _jsx(RoundActionIconButton, Object.assign({ size: "small", tooltipMsg: "Regenerate", onClick: handleRegenerate, baseColor: textColor, disabled: disabled }, { children: _jsx(CachedIcon, { fontSize: "small" }) }))] }));
};
export default AssistantContent;
